import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Popconfirm, Form, message, Row, Col, Select } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import AntDesignTable from "../common/antTable/AntDesignTable";
import CommonModal from "../common/commonModal/CommonModal";
import { API_URL } from "../../config";
import PatientForm from "./PatientForm";
import moment from "moment";
import PatientDetails from "../patientsComponents/PatientDetails";

const { Option } = Select;

const PatientsContainer = () => {
  const [patients, setPatients] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [form] = Form.useForm();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isNotificationDisabled, setIsNotificationDisabled] = useState(true);
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleDropdownChange = (value) => {
    setSelectedOption(value);
    setIsNotificationDisabled(false);
  };

const handleSendNotification = async () => {
  if (!selectedOption) return;

  setIsSendingNotification(true);

  try {
    const token = sessionStorage.getItem("authToken");
    const eventType = selectedOption.toUpperCase();

    const params = new URLSearchParams();
    params.append("title", "Notification Title");
    params.append("body", "Notification Body");

    const response = await axios.post(
      `${API_URL}/fcm/avat?event_type=${eventType}`,
      params.toString(),
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    message.success("Notification sent successfully");
    console.log("Notification sent:", response.data);
  } catch (error) {
    message.error("Failed to send notification");
  } finally {
    setIsSendingNotification(false);
    setSelectedOption(null);
    setIsNotificationDisabled(true);
  }
};



  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/?skip=0&limit=100`);
      setPatients(response.data);
    } catch (error) {
      console.error("There was an error fetching the patient data!", error);
    }
  };

  const fetchPatientDetails = async (patient_id) => {
    try {
      const response = await axios.get(`${API_URL}/user/${patient_id}`);
      const patientData = response.data;
      patientData.date_of_birth = moment(patientData.date_of_birth);
      setCurrentPatient(patientData);
      form.setFieldsValue(patientData);
    } catch (error) {
      console.error("There was an error fetching the patient details!", error);
    }
  };

  const handleEdit = async (record) => {
    await fetchPatientDetails(record.patient_id);
    setIsModalVisible(true);
  };

  const handleDelete = async (patient_id) => {
    try {
      await axios.delete(`${API_URL}/user/${patient_id}`);
      message.success("Patient deleted successfully");
      fetchPatients();
    } catch (error) {
      console.error("There was an error deleting the patient data!", error);
      message.error("Failed to delete the patient");
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const updatedPatient = {
        ...values,
        date_of_birth: values.date_of_birth.format("YYYY-MM-DD"),
      };

      await axios.patch(
        `${API_URL}/user/${currentPatient.patient_id}`,
        updatedPatient,
      );

      message.success("Patient updated successfully");
      setIsModalVisible(false);
      fetchPatients();
    } catch (error) {
      console.error("There was an error updating the patient data!", error);
      message.error("Failed to update the patient");
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <a onClick={() => handleDetails(record)}>
          {record.first_name} {record.last_name}
        </a>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: "Age",
      dataIndex: "age",
      key: "age",
      render: (_, record) => moment().diff(record.date_of_birth, "years"),
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
    },
    {
      title: "Email",
      dataIndex: "email_address",
      key: "email_address",
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <span>
          <Row gutter={[8, 8]}>
            <Col xs={12} sm={8} md={6}>
              <Button
                className="edit-button"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Col>

            <Col xs={12} sm={8} md={6}>
              <Popconfirm
                title="Are you sure you want to delete this patient?"
                onConfirm={() => handleDelete(record.patient_id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  className="delete-button"
                  icon={<DeleteOutlined />}
                  style={{ marginLeft: 8 }}
                />
              </Popconfirm>
            </Col>
          </Row>
        </span>
      ),
    },
  ];

  const handleDetails = (record) => {
    setSelectedPatient(record);
    console.log("Viewing details for:", record);
  };

  return (
    <React.Fragment>
      {selectedPatient ? (
        <PatientDetails patient={selectedPatient} />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="end">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Select option"
                style={{ width: "100%" }}
                onChange={handleDropdownChange}
                value={selectedOption}
                allowClear
                onClear={() => {
                  setSelectedOption(null);
                  setIsNotificationDisabled(true);
                }}
              >
                <Option value="ECP">ECP</Option>
                <Option value="Hospital">Hospital</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button
                type="primary"
                onClick={handleSendNotification}
                disabled={isNotificationDisabled || isSendingNotification}
                loading={isSendingNotification}
                style={{ width: "100%" }}
              >
                {isSendingNotification ? "Sending..." : "Send Notification"}
              </Button>
            </Col>
          </Row>

          <AntDesignTable columns={columns} tableData={patients} />
          <CommonModal
            title="Edit Patient"
            visible={isModalVisible}
            onCancel={handleCancel}
            onConfirm={handleOk}
            confirmButtonText="Submit"
          >
            <PatientForm form={form} formData={currentPatient} />
          </CommonModal>
        </>
      )}
    </React.Fragment>
  );
};

export default PatientsContainer;